/**
 * Friendly API errors.
 *
 * The API sends `{ success:false, error, code, action, retryable, requestId }`.
 * `code` is stable, so we translate by code; `error` is already safe to show
 * and is used when we have no better (translated) text. Raw library text
 * ("Network Error", "timeout of 30000ms exceeded", stack traces) is never shown.
 */
import { toast } from "react-toastify";
import i18n from "../i18n";

// Codes whose meaning is fully covered by our translated text. For the other
// codes (NOT_FOUND, CONFLICT, BAD_REQUEST…) the API's own sentence is more
// specific ("Campaign not found"), so it wins when present.
const TRANSLATED_CODES = new Set([
  "RATE_LIMITED",
  "TIMEOUT",
  "SERVICE_UNAVAILABLE",
  "INTERNAL_ERROR",
  "PAYLOAD_TOO_LARGE",
  "NOT_IMPLEMENTED",
  "LINKEDIN_SESSION_EXPIRED",
  "LINKEDIN_VERIFICATION_REQUIRED",
  "LINKEDIN_ACCOUNT_CHECK",
  "LINKEDIN_INVALID_CREDENTIALS",
  "LINKEDIN_PROXY_COUNTRY_REQUIRED",
  "LINKEDIN_CONNECTION_PROBLEM",
  "LINKEDIN_TEMPORARY",
  "LINKEDIN_INVALID_PROFILE_URL",
]);

// Situations that are usually temporary: shown as a softer warning, not an error.
const SOFT_ACTIONS = new Set(["retry", "wait"]);

const TECHNICAL_PATTERN =
  /net::|\berr_[a-z_]+|econn|etimedout|timeout of \d+ms|status code \d{3}|network error|prisma|typeerror|referenceerror|cannot read propert|is not a function|\bundefined\b|\s+at\s+\S+\s+\(|^cannot (get|post|put|patch|delete) \//i;

const GENERIC_PHRASES = new Set([
  "bad request",
  "unauthorized",
  "forbidden",
  "forbidden resource",
  "not found",
  "internal server error",
  "service unavailable",
  "service temporarily unavailable",
  "validation failed",
]);

export const looksTechnical = (message) => {
  if (typeof message !== "string" || !message.trim()) return true;
  const trimmed = message.trim();
  if (GENERIC_PHRASES.has(trimmed.toLowerCase().replace(/[.!]+$/, ""))) return true;
  return trimmed.length > 300 || TECHNICAL_PATTERN.test(trimmed);
};

const translate = (key, fallback) => (i18n.exists(key) ? i18n.t(key) : fallback);

const codeForStatus = (status) => {
  if (status === 400) return "BAD_REQUEST";
  if (status === 401) return "UNAUTHORIZED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 409) return "CONFLICT";
  if (status === 413) return "PAYLOAD_TOO_LARGE";
  if (status === 422) return "VALIDATION_FAILED";
  if (status === 429) return "RATE_LIMITED";
  if (status === 408 || status === 504) return "TIMEOUT";
  if (status === 502 || status === 503) return "SERVICE_UNAVAILABLE";
  if (status >= 500) return "INTERNAL_ERROR";
  return "BAD_REQUEST";
};

const formatValidationErrors = (validationErrors) => {
  if (!Array.isArray(validationErrors) || validationErrors.length === 0) return null;
  return validationErrors
    .map((entry) => {
      if (typeof entry === "string") return entry;
      if (entry?.field && entry?.constraints) {
        return `${entry.field}: ${Object.values(entry.constraints).join(", ")}`;
      }
      if (entry?.constraints) return Object.values(entry.constraints).join(", ");
      return null;
    })
    .filter(Boolean)
    .join("; ");
};

/**
 * Describe a failed request (raw axios error or the object our interceptor
 * rejects with) as `{ code, action, retryable, requestId, message }`.
 */
export const describeApiError = (err, fallback) => {
  if (err?.isApiError) return err;
  const axiosError = err?.originalError || err;
  const response = err?.response || axiosError?.response;
  const data = response?.data ?? err?.data;
  const status = response?.status ?? err?.status;
  const generic = fallback || translate("errors.generic", "Something didn't work. Please try again.");

  if (!response && !data) {
    const isRequestFailure = Boolean(axiosError?.isAxiosError || axiosError?.config);
    if (!isRequestFailure) {
      // Our own thrown Error (e.g. a proxy wait that gave up) with a written message.
      const own = typeof err?.message === "string" && !looksTechnical(err.message) ? err.message : generic;
      return { code: err?.code || "UNKNOWN", action: "none", retryable: false, message: own };
    }
    if (axiosError.code === "ERR_CANCELED") {
      return { code: "CANCELED", action: "none", retryable: false, message: "" };
    }
    const timedOut = axiosError.code === "ECONNABORTED" || axiosError.code === "ETIMEDOUT"
      || /timeout/i.test(axiosError.message || "");
    if (timedOut) {
      return {
        code: "CLIENT_TIMEOUT",
        action: "retry",
        retryable: true,
        message: translate("errors.clientTimeout", generic),
      };
    }
    return {
      code: "NETWORK",
      action: "retry",
      retryable: true,
      message: translate("errors.network", generic),
    };
  }

  const validation = formatValidationErrors(data?.validationErrors);
  const code = data?.code || (validation ? "VALIDATION_FAILED" : codeForStatus(status));
  const apiText = typeof data?.error === "string" ? data.error
    : typeof data?.error?.message === "string" ? data.error.message
      : typeof data?.message === "string" ? data.message
        : null;
  const translated = i18n.exists(`errors.codes.${code}`) ? i18n.t(`errors.codes.${code}`) : null;

  let message;
  if (code === "VALIDATION_FAILED") {
    const intro = translated || generic;
    message = validation ? `${intro} ${validation}` : (apiText && !looksTechnical(apiText) ? apiText : intro);
  } else if (TRANSLATED_CODES.has(code) && translated) {
    message = translated;
  } else if (apiText && !looksTechnical(apiText) && !(status >= 500 && !data?.code)) {
    message = apiText;
  } else {
    message = translated || generic;
  }

  return {
    code,
    action: data?.action || (status >= 500 ? "retry" : "none"),
    retryable: typeof data?.retryable === "boolean" ? data.retryable : status >= 500,
    requestId: data?.requestId || response?.headers?.["x-request-id"],
    message,
  };
};

/** The text to show for a failed request. */
export const getErrorMessage = (err, fallback) => describeApiError(err, fallback).message || fallback;

/**
 * The text to show for a failed background job event (e.g. IMPORT_PROGRESS
 * with status FAILED), which carries `{ message, code, action }`.
 */
export const getEventErrorMessage = (event, fallback) => {
  const code = event?.code;
  if (code && TRANSLATED_CODES.has(code) && i18n.exists(`errors.codes.${code}`)) {
    return i18n.t(`errors.codes.${code}`);
  }
  if (typeof event?.message === "string" && !looksTechnical(event.message)) return event.message;
  return fallback || translate("errors.generic", "Something didn't work. Please try again.");
};

/**
 * Show one calm toast for a failed request. Temporary problems use a warning
 * style; the same message is never stacked twice; unexpected server errors
 * carry a short reference to quote to support.
 */
export const notifyError = (err, fallback) => {
  const info = describeApiError(err, fallback);
  if (!info.message) return info;
  let text = info.message;
  if (info.action === "contact_support" && info.requestId) {
    text = `${text} ${i18n.t("errors.reference", { id: String(info.requestId).slice(0, 8) })}`;
  }
  const show = SOFT_ACTIONS.has(info.action) ? toast.warn : toast.error;
  show(text, { toastId: `api-error:${info.message}` });
  return info;
};

/** Toast for a failed background job event; see getEventErrorMessage. */
export const notifyEventError = (event, fallback) => {
  const message = getEventErrorMessage(event, fallback);
  const show = SOFT_ACTIONS.has(event?.action) ? toast.warn : toast.error;
  show(message, { toastId: `event-error:${message}` });
  return message;
};
